import type { RefObject, KeyboardEvent } from 'react';
import { User, Pencil, Check } from 'lucide-react';
import type { RankFa } from '../../domain/usecases/calcularRankFa';

const SHADOW_DEFAULT = '0 0 0 1px rgba(249,115,22,0.15), 0 25px 50px -12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.15)';

interface EditNomeProps {
  editing:   boolean;
  value:     string;
  inputRef:  RefObject<HTMLInputElement | null>;
  onChange:  (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSave:    () => void;
  onStart:   () => void;
}

interface Props {
  nome:                 string | null;
  phoneDisplay:         string;
  avatarUrl:            string | null;
  avatarBroken:         boolean;
  onAvatarError:        () => void;
  membroDesde:          string | null;
  rank:                 RankFa;
  topThumbnail:         string | null;
  progressoPctAnimado?: number;
  edit:                 EditNomeProps;
}

export function PerfilHeroCard({
  nome, phoneDisplay, avatarUrl, avatarBroken, onAvatarError,
  membroDesde, rank, topThumbnail, progressoPctAnimado, edit,
}: Props) {
  const progresso = progressoPctAnimado ?? rank.progressoPct;
  const rankLabel = rank.nome ?? 'Estreante em formação';
  const legenda = rank.faltamPedidos !== null
    ? `Faltam ${rank.faltamPedidos} ${rank.faltamPedidos === 1 ? 'pedido' : 'pedidos'} pro próximo nível`
    : 'Nível máximo';

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ boxShadow: SHADOW_DEFAULT }}>
      {/* Fundo blur da thumbnail do top 1, ou gradiente de fallback */}
      <div className="absolute inset-0 scale-110">
        {topThumbnail ? (
          <>
            <img src={topThumbnail} alt="" aria-hidden className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/70 backdrop-blur-2xl" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 to-rose-500/30" />
        )}
      </div>

      {/* Gradiente overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Conteúdo */}
      <div className="relative p-4">
        <div className="flex gap-3 items-center">
          <div className="relative shrink-0 w-14 h-14 rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-xl shadow-black/50 bg-gradient-to-br from-orange-500/20 to-rose-500/20 flex items-center justify-center">
            {avatarUrl && !avatarBroken ? (
              <img src={avatarUrl} alt="" className="w-full h-full object-cover" onError={onAvatarError} />
            ) : nome ? (
              <span className="text-lg font-bold text-orange-300">{nome.charAt(0).toUpperCase()}</span>
            ) : (
              <User size={24} className="text-orange-300" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-orange-300">{rankLabel}</span>
            </div>

            {edit.editing ? (
              <div className="flex items-center gap-2">
                <input
                  ref={edit.inputRef}
                  type="text"
                  value={edit.value}
                  onChange={e => edit.onChange(e.target.value)}
                  onKeyDown={edit.onKeyDown}
                  onBlur={edit.onSave}
                  maxLength={30}
                  aria-label="Nome de exibição"
                  className="flex-1 min-w-0 bg-white/[0.06] border border-white/[0.1] rounded-lg px-2.5 py-1 text-base font-bold text-white focus:outline-none focus:border-orange-500/50"
                />
                <button aria-label="Salvar nome" onClick={edit.onSave} className="flex h-11 w-11 items-center justify-center text-orange-300 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 rounded-lg">
                  <Check size={18} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <p className="text-lg font-bold text-white truncate">{nome || 'Usuário Jukebox'}</p>
                <button aria-label="Editar nome" onClick={edit.onStart} className="flex h-11 w-11 items-center justify-center text-white/55 hover:text-white/70 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 rounded-lg">
                  <Pencil size={13} />
                </button>
              </div>
            )}

            <p className="text-white/50 text-xs mt-0.5">{phoneDisplay}</p>
          </div>
        </div>

        {/* Barra de progresso de rank */}
        <div className="mt-4">
          <div className="relative h-1 rounded-full bg-white/15 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-orange-400 to-rose-400"
              style={{ width: `${progresso}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <span className="text-white/55 text-[11px]">{legenda}</span>
            {membroDesde && (
              <span className="text-white/50 text-[10px] shrink-0">
                Membro desde {new Date(membroDesde).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
